import { MAPBOX_ACCESS_TOKEN } from './adminMapConfig';

const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 300;

/** Max encoded geojson length for Mapbox Static Images overlay URLs. */
const MAX_STATIC_OVERLAY_CHARS = 4000;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string' && result.startsWith('data:image')) {
        resolve(result);
      } else {
        reject(new Error('failed to read thumbnail blob'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

/** Simple schematic preview when Mapbox static fetch is unavailable. */
export function generateSvgGeopolygonThumbnail(
  ring: number[][],
  width: number = DEFAULT_WIDTH,
  height: number = DEFAULT_HEIGHT,
): string {
  const lngs = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  let minLng = Math.min(...lngs);
  let maxLng = Math.max(...lngs);
  let minLat = Math.min(...lats);
  let maxLat = Math.max(...lats);
  const padLng = (maxLng - minLng) * 0.1 || 0.01;
  const padLat = (maxLat - minLat) * 0.1 || 0.01;
  minLng -= padLng;
  maxLng += padLng;
  minLat -= padLat;
  maxLat += padLat;
  const spanLng = maxLng - minLng || 1;
  const spanLat = maxLat - minLat || 1;

  const margin = 16;
  const toX = (lng: number) =>
    margin + ((lng - minLng) / spanLng) * (width - margin * 2);
  const toY = (lat: number) =>
    margin + (1 - (lat - minLat) / spanLat) * (height - margin * 2);

  const pointsAttr = ring
    .map(([lng, lat]) => `${toX(lng).toFixed(1)},${toY(lat).toFixed(1)}`)
    .join(' ');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="#e8ecf0"/>
  <polygon points="${pointsAttr}" fill="rgba(59,178,208,0.35)" stroke="#2a7a8f" stroke-width="2"/>
</svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

async function fetchMapboxStaticThumbnail(
  ring: number[][],
  width: number,
  height: number,
): Promise<string> {
  const feature = {
    type: 'Feature',
    properties: {
      stroke: '#2a7a8f',
      'stroke-width': 2,
      fill: '#3bb2d0',
      'fill-opacity': 0.25,
    },
    geometry: {
      type: 'Polygon',
      coordinates: [ring],
    },
  };

  const overlay = `geojson(${encodeURIComponent(JSON.stringify(feature))})`;
  const url =
    `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/` +
    `${overlay}/auto/${width}x${height}?access_token=${MAPBOX_ACCESS_TOKEN}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Mapbox static image failed (${response.status})`);
  }
  return blobToDataUrl(await response.blob());
}

/**
 * Build a sidebar map preview for a geopolygon ring.
 * Uses Mapbox Static Images; falls back to a local SVG schematic.
 */
export async function generateGeopolygonMapThumbnail(
  ring: number[][],
  options?: { width?: number; height?: number },
): Promise<string> {
  if (ring.length < 3) {
    throw new Error('geopolygon ring must have at least 3 points');
  }

  const width = options?.width ?? DEFAULT_WIDTH;
  const height = options?.height ?? DEFAULT_HEIGHT;

  const featureProbe = {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ring] },
    properties: {
      stroke: '#2a7a8f',
      'stroke-width': 2,
      fill: '#3bb2d0',
      'fill-opacity': 0.25,
    },
  };
  const overlayChars = encodeURIComponent(JSON.stringify(featureProbe)).length;
  if (overlayChars > MAX_STATIC_OVERLAY_CHARS) {
    return generateSvgGeopolygonThumbnail(ring, width, height);
  }

  try {
    return await fetchMapboxStaticThumbnail(ring, width, height);
  } catch (err) {
    console.warn(
      'Mapbox static geopolygon thumbnail failed, using SVG fallback:',
      err,
    );
    return generateSvgGeopolygonThumbnail(ring, width, height);
  }
}

/** Shared thumbnail pipeline for draw + URL hydration. */
export async function applyGeopolygonThumbnail(ring: number[][]): Promise<string> {
  const img = await generateGeopolygonMapThumbnail(ring);
  if (!img.startsWith('data:image')) {
    throw new Error('geopolygon thumbnail generation returned invalid data');
  }
  return img;
}
